import React from 'react';
import dayjs from 'dayjs';
import { ArticleShare } from './article-share';
import { plainText, articleSource, pressSource } from './text';
import { useTranslation } from 'next-i18next';
import MetaTag from '@/components/ui/MetaTag';
import JsonLd from '@/components/ui/JsonLd';
import { BUSINESS } from '@/constants/business';
import { isArticleNoindex } from '@/constants/offTopicArticles';
import { IArticle } from '@/features/article/layout';
import RelatedProduct from '@/features/article/partial/RelatedProduct';
import AmbassadorRegistrationForm from '@/features/article/partial/ambassador-registration/AmbassadorRegistrationForm';
import { TOPIC_BY_SLUG, topicHref } from './const';
import { A, ArticleGrid, Breadcrumb, Eyebrow, JournalShell, Section, SectionHead } from './components';
import type { JournalArticle } from './const';

const SITE = BUSINESS.appUrl;
const plain = plainText;

export type JournalArticleProps = IArticle & {
  /** chuyên đề của bài (nếu phân loại được) */
  topicSlug?: string | null;
  /** bố cục khuyến mại: có ảnh bìa lớn */
  cover?: boolean;
};

/** Trang bài viết trong khung An Thư Journal: cùng header/footer với /tin-tuc/, có tiêu đề hiển thị và đường quay về. */
export const JournalArticleLayout = ({ article, products, newArticles, topicSlug, cover }: JournalArticleProps) => {
  const { t } = useTranslation(['layout']);
  const { yoast_head_json, title, thumbnail_url, content } = article;
  const topic = topicSlug ? TOPIC_BY_SLUG[topicSlug] : undefined;
  const heading = plain(title.rendered);
  const url = `${SITE}/article/${article.slug}`;
  const isPress = article.categories?.includes(27);
  const originalSource = pressSource(content.rendered, isPress);
  const source = originalSource || articleSource(article.link);
  const crumbs = [
    { label: 'Trang chủ', href: '/' },
    { label: 'Tin tức', href: '/tin-tuc/' },
    ...(isPress ? [{ label: 'Báo chí', href: '/tin-tuc/bao-chi/' }] : []),
    ...(topic ? [{ label: topic.title, href: topicHref(topic.slug) }] : []),
    { label: heading },
  ];
  const more: JournalArticle[] = (newArticles || [])
    .filter((n) => n.id !== article.id)
    .sort((a, b) => {
      const overlap = (item: typeof a) => (item.categories || []).filter((category) => article.categories?.includes(category)).length;
      return overlap(b) - overlap(a);
    })
    .slice(0, 3)
    .map((n) => ({
      id: n.id,
      slug: n.slug,
      title: plain(n.title.rendered),
      excerpt: '',
      date: n.date,
      image: n.thumbnail_url || null,
      topics: [],
    }));
  const navKey = isPress ? 'bao-chi' : topic && ['kim-cuong', 'trang-suc'].includes(topic.slug) ? topic.slug : 'kien-thuc';

  return (
    <JournalShell activeKey={navKey}>
      <MetaTag
        title={heading}
        noindex={isArticleNoindex(article)}
        description={yoast_head_json?.description}
        seo={{ title: heading, description: yoast_head_json?.description, keywords: article.acf?.keywords }}
        image={thumbnail_url}
      />
      <JsonLd
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: heading,
            ...(originalSource ? { citation: originalSource.href } : {}),
            image: thumbnail_url,
            datePublished: article.date,
            dateModified: article.modified,
            author: { '@type': 'Organization', '@id': BUSINESS.id, name: BUSINESS.name },
            publisher: { '@type': 'Organization', name: BUSINESS.name, logo: { '@type': 'ImageObject', url: BUSINESS.logo } },
            mainEntityOfPage: { '@type': 'WebPage', '@id': url },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Trang chủ', item: SITE },
              { '@type': 'ListItem', position: 2, name: 'Tin tức', item: `${SITE}/tin-tuc/` },
              ...(isPress ? [{ '@type': 'ListItem', position: 3, name: 'Báo chí', item: `${SITE}/tin-tuc/bao-chi/` }] : []),
              ...(topic ? [{ '@type': 'ListItem', position: isPress ? 4 : 3, name: topic.title, item: `${SITE}${topicHref(topic.slug)}` }] : []),
              { '@type': 'ListItem', position: 3 + (isPress ? 1 : 0) + (topic ? 1 : 0), name: heading, item: url },
            ],
          },
        ]}
      />
      <Breadcrumb items={crumbs} />
      <div className={`tw-mx-auto tw-w-full tw-max-w-[1296px] tw-px-5 md:tw-px-8 tw-grid tw-grid-cols-1 ${more.length ? 'lg:tw-grid-cols-[minmax(0,1fr)_280px] xl:tw-grid-cols-[minmax(0,1fr)_320px]' : ''} tw-gap-10 xl:tw-gap-16`}>
      <article className="tw-min-w-0">
        <header className="tw-w-full tw-pb-8">
          <Eyebrow>{isPress ? 'BÁO CHÍ' : topic ? topic.tag : 'TIN TỨC'}</Eyebrow>
          <h1
            className="tw-font-jr tw-mt-4 tw-mb-0 tw-text-[30px] md:tw-text-[44px] tw-font-normal tw-leading-[1.2] [text-wrap:balance]"
          >{heading}</h1>
          {dayjs(article.date).isValid() && (
            <p className="tw-mt-4 tw-mb-0 tw-text-[14px] tw-text-jr-mute">
              <time dateTime={article.date}>{dayjs(article.date).format('DD/MM/YYYY')}</time>
            </p>
          )}
          {source && <p className="tw-mt-2 tw-mb-0 tw-text-sm tw-text-jr-mute">Nguồn: <a href={source.href} target="_blank" rel="noopener noreferrer" className="tw-text-jr-green hover:tw-underline">{source.label} ↗</a></p>}
        </header>
        {cover && thumbnail_url && (
          <div className="tw-w-full tw-pb-8">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={thumbnail_url} alt={heading} className="tw-w-full tw-h-auto tw-max-h-[520px] tw-object-cover" />
          </div>
        )}
        <div className="tw-w-full">
          <div
            className="tw-text-jr-ink tw-w-full tw-max-w-none tw-prose tw-prose-sm md:tw-prose-base lg:tw-prose-lg content-article"
            dangerouslySetInnerHTML={{ __html: content.rendered }}
          />
          <div className="tw-mt-8 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 tw-border-t tw-border-jr-line tw-pt-6">
            {topic && <A href={topicHref(topic.slug)} className="tw-text-[15px] tw-font-medium tw-text-jr-green hover:tw-underline">
              ← Về chuyên đề {topic.title}
            </A>}
            <ArticleShare url={url} title={heading} />
          </div>
        </div>
      </article>
      {more.length > 0 && <aside aria-label="Bài viết liên quan" className="tw-min-w-0">
        <div className="lg:tw-sticky lg:tw-top-6 tw-border-t lg:tw-border-t-0 tw-border-jr-line tw-pt-6 lg:tw-pt-0">
          <Eyebrow>KHÁM PHÁ THÊM</Eyebrow>
          <h2 className="tw-font-jr tw-text-2xl tw-mt-3 tw-mb-6">Bài viết liên quan</h2>
          {more.map((item) => <A key={item.id} href={`/article/${item.slug}/`} className="tw-block tw-mb-6 tw-pb-6 tw-border-b tw-border-jr-line tw-group">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            {item.image && <img src={item.image} alt="" loading="lazy" className="tw-w-full tw-aspect-[16/9] tw-object-cover tw-mb-3" />}
            <span className="tw-text-xs tw-text-jr-mute">{dayjs(item.date).format('DD/MM/YYYY')}</span>
            <h3 className="tw-font-jr tw-text-xl tw-leading-snug tw-mt-2 tw-mb-0 group-hover:tw-text-jr-green">{item.title}</h3>
          </A>)}
        </div>
      </aside>}
      </div>
      <div className="tw-mx-auto tw-w-full tw-max-w-[1296px] tw-px-5 md:tw-px-8">
        <AmbassadorRegistrationForm article={article} />
        {!!products?.length && (
          <div className="tw-mt-10 tw-hidden lg:tw-block">
            <RelatedProduct products={products} t={t} />
          </div>
        )}
      </div>
      {more.length > 0 && (
        <Section className="!tw-pb-4">
          <SectionHead title="Tin mới nhất tại An Thư" />
          <ArticleGrid items={more} showDate />
        </Section>
      )}
    </JournalShell>
  );
};
